import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile, toBlobURL } from '@ffmpeg/util';

/**
 * Convertit un fichier WebM en MP4 en utilisant ffmpeg.wasm côté client
 */
export class WebMToMP4Converter {
  private ffmpeg: FFmpeg | null = null;
  private isLoaded = false;

  /**
   * Charge ffmpeg.wasm (à faire une seule fois)
   */
  async load(onProgress?: (progress: number) => void): Promise<void> {
    if (this.isLoaded) return;

    this.ffmpeg = new FFmpeg();

    // Gestion des logs ffmpeg
    this.ffmpeg.on('log', ({ message }) => {
      console.log('[FFmpeg]', message);
    });

    // Gestion de la progression
    if (onProgress) {
      this.ffmpeg.on('progress', ({ progress }) => {
        onProgress(Math.round(progress * 100));
      });
    }

    // Chargement des fichiers core de ffmpeg.wasm depuis CDN
    const baseURL = 'https://unpkg.com/@ffmpeg/core@0.12.6/dist/esm';
    await this.ffmpeg.load({
      coreURL: await toBlobURL(`${baseURL}/ffmpeg-core.js`, 'text/javascript'),
      wasmURL: await toBlobURL(`${baseURL}/ffmpeg-core.wasm`, 'application/wasm'),
    });

    this.isLoaded = true;
    console.log('[FFmpeg] Chargé avec succès');
  }

  /**
   * Convertit un Blob WebM en Blob MP4
   */
  async convert(
    webmBlob: Blob,
    onProgress?: (progress: number) => void
  ): Promise<Blob> {
    if (!this.ffmpeg || !this.isLoaded) {
      throw new Error('FFmpeg n\'est pas chargé. Appelez load() d\'abord.');
    }

    try {
      // Écrire le fichier WebM en mémoire
      console.log('[FFmpeg] Écriture du fichier WebM en mémoire...');
      const inputData = await fetchFile(webmBlob);
      await this.ffmpeg.writeFile('input.webm', inputData);

      // Conversion WebM → MP4
      console.log('[FFmpeg] Démarrage de la conversion...');
      await this.ffmpeg.exec([
        '-i', 'input.webm',
        '-c:v', 'libx264',      // Codec vidéo H.264
        '-pix_fmt', 'yuv420p',  // Standard compatible macOS/QuickTime/YouTube/iOS
        '-preset', 'medium',     // Preset de compression
        '-crf', '23',            // Qualité (18-28, 23 = bon équilibre)
        '-c:a', 'aac',           // Codec audio AAC
        '-b:a', '128k',          // Bitrate audio 128 kbps
        '-movflags', '+faststart', // Optimisation pour streaming web
        'output.mp4'
      ]);

      // Lire le fichier MP4 résultant
      console.log('[FFmpeg] Lecture du fichier MP4...');
      const mp4Data = await this.ffmpeg.readFile('output.mp4');

      // Nettoyer les fichiers temporaires
      await this.ffmpeg.deleteFile('input.webm');
      await this.ffmpeg.deleteFile('output.mp4');

      // Convertir en Blob
      const mp4Blob = new Blob([mp4Data as any], { type: 'video/mp4' });
      console.log('[FFmpeg] Conversion terminée avec succès');

      return mp4Blob;

    } catch (error) {
      console.error('[FFmpeg] Erreur lors de la conversion:', error);
      throw new Error(`Échec de la conversion: ${error instanceof Error ? error.message : 'Erreur inconnue'}`);
    }
  }

  /**
   * Nettoie les ressources ffmpeg
   */
  async cleanup(): Promise<void> {
    if (this.ffmpeg && this.isLoaded) {
      await this.ffmpeg.terminate();
      this.ffmpeg = null;
      this.isLoaded = false;
      console.log('[FFmpeg] Nettoyage effectué');
    }
  }
}

// Instance singleton pour réutilisation
let converterInstance: WebMToMP4Converter | null = null;

/**
 * Récupère l'instance singleton du convertisseur
 */
export function getConverter(): WebMToMP4Converter {
  if (!converterInstance) {
    converterInstance = new WebMToMP4Converter();
  }
  return converterInstance;
}
